import React, { useState, useEffect } from 'react';
import { UserPlus, Phone, Landmark, X, Trash2, Edit3, Mail, MapPin, Search } from 'lucide-react';
import api from '../api/axiosInstance';

const Directory = () => {
    const [type, setType] = useState('client'); // 'client' or 'vendor'
    const [list, setList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Form State
    const [formData, setFormData] = useState({
        id: null, name: '', contact_person: '', phone: '', email: '',
        gst_pan: '', address: '', bank_name: '', account_no: '', ifsc_code: ''
    });

    // --- 1. Fetch List Based on Type ---
    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await api.get(`get_directory.php?type=${type}`);
            if (res.data.status === 'success') {
                setList(res.data.data);
            }
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, [type]);

    // --- 2. Save / Update Logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const endpoint = formData.id ? 'update_directory.php' : 'save_directory.php';
            const res = await api.post(endpoint, { ...formData, entity_type: type });
            if (res.data.status === 'success') {
                alert(`${type === 'client' ? 'Client' : 'Vendor'} saved successfully!`);
                setIsModalOpen(false);
                resetForm();
                fetchList();
            }
        } catch (err) {
            alert("Error saving record");
        }
    };

    // --- 3. Delete Logic ---
    const handleDelete = async (id) => {
        if (window.confirm("Are you sure? This will affect linked invoices.")) {
            try {
                const res = await api.post('delete_directory.php', { id });
                if (res.data.status === 'success') {
                    fetchList();
                } else {
                    alert(res.data.message || "Delete failed");
                }
            } catch (err) {
                alert("Delete failed");
            }
        }
    };

    const resetForm = () => {
        setFormData({ id: null, name: '', contact_person: '', phone: '', email: '', gst_pan: '', address: '', bank_name: '', account_no: '', ifsc_code: '' });
    };

    const openEdit = (item) => {
        setFormData(item);
        setIsModalOpen(true);
    };

    const filteredList = list.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.gst_pan.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">Master Directory</h1>
                    <p className="text-slate-500 font-medium">Manage your {type}s ecosystem.</p>
                </div>
                <button onClick={() => { resetForm(); setIsModalOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-2 shadow-xl shadow-indigo-100 transition-all">
                    <UserPlus size={18} /> Add {type === 'client' ? 'Client' : 'Vendor'}
                </button>
            </div>

            {/* Toggle & Search */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex bg-slate-100 p-1.5 rounded-2xl w-fit border border-slate-200">
                    <button onClick={() => setType('client')} className={`px-8 py-3 rounded-xl font-bold transition-all ${type === 'client' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Clients</button>
                    <button onClick={() => setType('vendor')} className={`px-8 py-3 rounded-xl font-bold transition-all ${type === 'vendor' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Vendors</button>
                </div>
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                    <input
                        type="text" placeholder={`Search ${type}s...`}
                        className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-50 font-medium"
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Entity List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {loading ? (
                    <div className="col-span-2 py-20 text-center font-black text-slate-200 text-4xl uppercase italic tracking-widest animate-pulse">Syncing Directory...</div>
                ) : filteredList.map(item => (
                    <div key={item.id} className="bg-white rounded-[40px] p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
                        <div className="flex justify-between items-start mb-6">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-xl border border-indigo-100">
                                    {item.name[0]}
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 leading-tight">{item.name}</h3>
                                    <p className="text-xs font-bold text-indigo-500 uppercase tracking-widest">{item.gst_pan || 'No GST/PAN'}</p>
                                </div>
                            </div>
                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                <button onClick={() => openEdit(item)} className="p-3 bg-slate-50 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"><Edit3 size={18} /></button>
                                <button onClick={() => handleDelete(item.id)} className="p-3 bg-slate-50 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"><Trash2 size={18} /></button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm font-medium text-slate-500">
                            <div className="flex items-center gap-2"><Phone size={14} /> {item.phone}</div>
                            <div className="flex items-center gap-2"><Mail size={14} /> {item.email}</div>
                            <div className="col-span-2 flex items-start gap-2"><MapPin size={14} className="mt-1 shrink-0" /> {item.address}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* MODAL: ADD/EDIT */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <div className="bg-white w-full max-w-4xl rounded-[48px] shadow-2xl p-12 overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center mb-10">
                            <h2 className="text-3xl font-black italic">{formData.id ? 'Edit' : 'Register'} {type}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="p-3 bg-slate-100 rounded-2xl hover:bg-rose-50 transition-all"><X /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-6">
                            <input required value={formData.name} placeholder="Company Name" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none focus:bg-white border border-transparent focus:border-indigo-100" onChange={e => setFormData({ ...formData, name: e.target.value })} />
                            <input value={formData.contact_person} placeholder="Contact Person" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, contact_person: e.target.value })} />
                            <input required value={formData.phone} placeholder="Phone Number" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                            <input value={formData.email} placeholder="Email" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, email: e.target.value })} />
                            <input value={formData.gst_pan} placeholder="GST / PAN No." className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" onChange={e => setFormData({ ...formData, gst_pan: e.target.value })} />
                            <textarea required value={formData.address} placeholder="Full Address" className="p-4 bg-slate-50 rounded-2xl font-bold col-span-2 outline-none h-32" onChange={e => setFormData({ ...formData, address: e.target.value })} />

                            <div className="col-span-2 p-8 bg-slate-900 rounded-[40px] space-y-6 text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-8 opacity-10"><Landmark size={100} /></div>
                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] relative z-10">Banking Infrastructure</p>
                                <div className="grid grid-cols-3 gap-6 relative z-10">
                                    <input value={formData.bank_name} placeholder="Bank Name" className="bg-white/10 border border-white/10 p-4 rounded-2xl text-sm font-bold outline-none focus:bg-white/20" onChange={e => setFormData({ ...formData, bank_name: e.target.value })} />
                                    <input value={formData.account_no} placeholder="A/C Number" className="bg-white/10 border border-white/10 p-4 rounded-2xl text-sm font-bold outline-none focus:bg-white/20" onChange={e => setFormData({ ...formData, account_no: e.target.value })} />
                                    <input value={formData.ifsc_code} placeholder="IFSC Code" className="bg-white/10 border border-white/10 p-4 rounded-2xl text-sm font-bold outline-none focus:bg-white/20" onChange={e => setFormData({ ...formData, ifsc_code: e.target.value })} />
                                </div>
                            </div>
                            <button type="submit" className="col-span-2 py-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-[32px] font-black text-xl shadow-xl shadow-indigo-100 transition-all active:scale-95">
                                {formData.id ? 'Apply Updates' : 'Commit to Directory'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Directory;